import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';
import AddressAutocomplete from '../components/AddressAutocomplete';
import '../styles/Booking.css';

interface AppSummary {
  id: string;
  name: string;
  icon: string;
  hours: string;
  email: string;
  minAge?: number;
}

const Booking: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [app, setApp] = useState<AppSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [booked, setBooked] = useState(false);
  
  // Form State
  const [address, setAddress] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [startTime, setStartTime] = useState('04:00');
  const [startAmPm, setStartAmPm] = useState('PM');
  const [endTime, setEndTime] = useState('06:00');
  const [endAmPm, setEndAmPm] = useState('PM');
  const [childAge, setChildAge] = useState('');
  const [validationError, setValidationError] = useState('');
  const [durationNote, setDurationNote] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/signup');
      return;
    }

    fetch(`${API_URL}/api/apps/${id}`)
      .then(res => res.json())
      .then(data => {
        if (data.error) setApp(null);
        else setApp(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching sitter info:', err);
        setLoading(false);
      });
  }, [id, user, navigate]);

  const getDayLimits = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDay();
    if (day >= 1 && day <= 4) return { start: "15:20", end: "21:00", label: '3:20 PM – 9:00 PM' };
    if (day === 5) return { start: "15:20", end: "21:30", label: '3:20 PM – 9:30 PM' };
    if (day === 6) return { start: "10:00", end: "21:30", label: '10:00 AM – 9:30 PM' };
    return { start: "10:00", end: "21:00", label: '10:00 AM – 9:00 PM' };
  };

  const toMinutes = (timeString: string, ampm?: string) => {
    let [hours, minutes] = timeString.split(':').map(Number);
    if (ampm) {
      if (ampm === 'PM' && hours !== 12) hours += 12;
      if (ampm === 'AM' && hours === 12) hours = 0;
    }
    return hours * 60 + minutes;
  };

  useEffect(() => {
    let error = '';
    
    // Date Validation (New: 1 day notice)
    if (selectedDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const bookingDate = new Date(selectedDate);
      bookingDate.setHours(0, 0, 0, 0);
      
      const diffTime = bookingDate.getTime() - today.getTime();
      const diffDays = diffTime / (1000 * 60 * 60 * 24);

      if (diffDays < 1) {
        error = 'Invalid date: You must provide at least 24 hours notice (bookings must be for tomorrow or later).';
      } else {
        // Time Validation (only if date is valid)
        const limits = getDayLimits(selectedDate);
        const limitStartMins = toMinutes(limits.start);
        const limitEndMins = toMinutes(limits.end);
        const startMins = toMinutes(startTime, startAmPm);
        const endMins = toMinutes(endTime, endAmPm);

        if (startMins < limitStartMins || startMins > limitEndMins || endMins < limitStartMins || endMins > limitEndMins) {
          error = `Invalid time: Selected time must be within ${limits.label}`;
        } else if (endMins <= startMins) {
          error = 'Invalid time: End time must be after start time.';
        } else if (endMins - startMins < 60) {
          error = 'Invalid time: Babysitting requires a 1-hour minimum.';
        }
      }

      // Duration Note (New Rule)
      const startMins = toMinutes(startTime, startAmPm);
      const endMins = toMinutes(endTime, endAmPm);
      const durationHours = (endMins - startMins) / 60;
      if (durationHours > 5) {
        setDurationNote('Note: For sessions over 5 hours, an extra fee applies. If you need significantly more time, please contact us to discuss special rates.');
      } else {
        setDurationNote('');
      }
    }

    // Age Validation
    if (childAge) {
      const age = parseInt(childAge);
      if (age < 1 || age > 10) {
        error = 'Invalid age: Sitters only care for children between 1 and 10 years old.';
      } else if (app?.minAge && age < app.minAge) {
        error = `Invalid age: ${app.name} requires children to be at least ${app.minAge} years old.`;
      }
    }

    setValidationError(error);
  }, [selectedDate, startTime, startAmPm, endTime, endAmPm, childAge, app]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (validationError) return;

    // Address Validation for Nova Scotia
    const nsRegex = /(Nova Scotia|NS|B[0-9][A-Z] [0-9][A-Z][0-9])/i;
    if (!nsRegex.test(address)) {
      setValidationError('Invalid address: Our sitters currently only operate within Nova Scotia. Please include "NS" or "Nova Scotia" in your address.');
      return;
    }

    const formData = new FormData(e.currentTarget);
    const bookingData = {
      sitterId: id,
      sitterName: app?.name,
      sitterEmail: app?.email,
      parentName: user?.name,
      parentEmail: user?.email,
      parentPhone: user?.phone,
      address: address,
      date: formData.get('date'),
      startTime: `${startTime} ${startAmPm}`,
      endTime: `${endTime} ${endAmPm}`,
      childName: formData.get('childName'),
      childAge: childAge,
      allergies: formData.get('allergies'),
      specialInstructions: formData.get('specialInstructions'),
    };

    fetch(`${API_URL}/api/bookings`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(bookingData),
    })
      .then(res => res.json())
      .then(() => {
        setBooked(true);
        setTimeout(() => { navigate(`/app/${id}`); }, 3000);
      })
      .catch(err => {
        console.error('Error saving booking:', err);
        setValidationError('Failed to send booking request. Please try again.');
      });
  };

  if (!user) return null;
  if (loading) return <div className="booking-container"><div className="loading-state">Loading...</div></div>;
  if (!app) return <div className="booking-container"><h2>Sitter not found</h2></div>;

  return (
    <div className="booking-container">
      <Link to={`/app/${id}`} className="back-link">← Back to Profile</Link>
      <div className="booking-card">
        <div className="booking-header">
          <img src={app.icon} alt={app.name} className="booking-icon" />
          <h1>Book {app.name}</h1>
        </div>

        {booked ? (
          <div className="success-msg">
            <h2>🎉 Request Sent!</h2>
            <p>An email has been sent to {app.name}. They will confirm your booking shortly.</p>
          </div>
        ) : (
          <form className="booking-form" onSubmit={handleSubmit}>
            <div className="user-info-summary">
              <p>Booking as: <strong>{user.name}</strong> ({user.email})</p>
            </div>

            <div className="form-group">
              <label>Home Address</label>
              <AddressAutocomplete 
                value={address} 
                onChange={setAddress} 
                placeholder="e.g. 123 Maple St, Apt 4" 
                required 
              />
              {app?.name.includes('Ian') && (
                <div style={{ color: '#e67e22', fontSize: '0.85rem', marginTop: '5px', fontWeight: 'bold' }}>
                  ⚠️ Note: Ian requires an extra travel fee if you are outside his local area.
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Date</label>
              <input name="date" type="date" required value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
              {selectedDate && <div className="availability-hint">Sitter's Hours: <strong>{getDayLimits(selectedDate).label}</strong></div>}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Start Time</label>
                <div className="time-select-group">
                  <select value={startTime} onChange={(e) => setStartTime(e.target.value)}>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(h => (
                      <React.Fragment key={h}>
                        <option value={`${h.toString().padStart(2, '0')}:00`}>{h}:00</option>
                        <option value={`${h.toString().padStart(2, '0')}:30`}>{h}:30</option>
                      </React.Fragment>
                    ))}
                  </select>
                  <select value={startAmPm} onChange={(e) => setStartAmPm(e.target.value)}>
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>End Time</label>
                <div className="time-select-group">
                  <select value={endTime} onChange={(e) => setEndTime(e.target.value)}>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(h => (
                      <React.Fragment key={h}>
                        <option value={`${h.toString().padStart(2, '0')}:00`}>{h}:00</option>
                        <option value={`${h.toString().padStart(2, '0')}:30`}>{h}:30</option>
                      </React.Fragment>
                    ))}
                  </select>
                  <select value={endAmPm} onChange={(e) => setEndAmPm(e.target.value)}>
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </div>
              </div>
            </div>

            {validationError && <div className="auth-error" style={{marginTop: 0}}>{validationError}</div>}
            {durationNote && !validationError && <div className="duration-note" style={{ color: '#666', fontSize: '0.85rem', marginBottom: '15px', fontStyle: 'italic', padding: '10px', backgroundColor: '#fdfaea', borderRadius: '8px', borderLeft: '4px solid #f1c40f' }}>{durationNote}</div>}

            <div className="form-group">
              <label>Child's Name</label>
              <input name="childName" type="text" placeholder="e.g. Charlie" required />
            </div>

            <div className="form-group">
              <label>Child's Age ({app?.minAge ? `Must be between ${app.minAge} and 10` : 'Must be between 1 and 10'})</label>
              <input 
                name="childAge" 
                type="number" 
                placeholder="e.g. 4" 
                required 
                min="1" 
                max="10"
                value={childAge}
                onChange={(e) => setChildAge(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Allergies / Medical Info</label>
              <textarea name="allergies" placeholder="e.g. Peanuts, asthma inhaler in bag..."></textarea>
            </div>

            <div className="form-group">
              <label>Special Instructions</label>
              <textarea name="specialInstructions" placeholder="e.g. Favorite bedtime story, favorite snacks..."></textarea>
            </div>

            <button type="submit" className="book-submit-btn" disabled={!!validationError} style={{opacity: validationError ? 0.5 : 1}}>
              Send Booking Request
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default Booking;
